import { countBy, isEqual, mapKeys, mapValues, transform } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getDocSnapshot, updateFirestoreDoc } from "../firebase";
import { playlistData } from "../playlistData";
import { PLAYER_STATUS } from "../constants";

const SESSION_SLIDES_COLLECTION = "sessionSlides";

const useSession = (sessionId) => {
  const [sessionData, setSessionData] = useState({});
  const [sessionPollResultsData, setSessionPollResultsData] = useState({});
  const [sessionSlidesData, setSessionSlidesData] = useState({});
  const [pollResults, setPollResults] = useState({});
  const [pollsNumCompleted, setPollsNumCompleted] = useState({});
  const [customizationNumCompleted, setCustomizationNumCompleted] = useState(0);
  const [isClassPause, setIsClassPause] = useState(true);
  const [pollId, setPollId] = useState(null);

  useEffect(() => {
    return getDocSnapshot("sessions", sessionId, (snapshot) => {
      /** @todo update identifier isClassPause to better represent its value, boolean or string (discuss slide id) */
      const {
        __preview: isPreview,
        isClassPause,
        pollId,
        ...rest
      } = snapshot.data();

      setSessionData({ isPreview, ...rest });
      // if !isPreview sync isClassPause and pollId with session doc data
      if (!isPreview) {
        setIsClassPause(isClassPause);
        setPollId(pollId);
      }
    });
  }, [sessionId]);

  /** @todo update identifier sessionPolls and sessionPollsId after updating firestore collection name */
  const {
    lessonId = null,
    sessionPolls: sessionPollsId,
    sessionSlides: sessionSlidesId,
    sessionStats: sessionStatsId,
    isPreview,
  } = sessionData;

  /*
    SESSION POLLS
  */

  const pollsData = useMemo(
    () => playlistData?.[lessonId]?.data?.polls ?? {},
    [lessonId]
  );

  useEffect(() => {
    if (sessionPollsId) {
      const unsubscribe = getDocSnapshot(
        "sessionPolls",
        sessionPollsId,
        (snapshot) => {
          const { results = {}, customizationCompleted = {} } = snapshot.data();
          setCustomizationNumCompleted(
            Object.keys(customizationCompleted).length
          );
          if (!isEqual(results, sessionPollResultsData)) {
            setSessionPollResultsData(results);
            setPollsNumCompleted(
              mapValues(results, (pollResult) => Object.keys(pollResult).length)
            );
            setPollResults(getPollResults(results, pollsData));
          }
        }
      );

      return unsubscribe;
    } else {
      const emptyPollResults = {};
      for (const pollId in pollsData) {
        emptyPollResults[pollId] = {};
      }
      setPollResults(getPollResults(emptyPollResults, pollsData));
    }
  }, [pollsData, sessionPollsId, sessionPollResultsData]);

  const setPauseAndClearPrevPoll = useCallback(
    (isClassPause) => {
      if (!sessionId) {
        return;
      }

      if (isPreview) {
        setIsClassPause(isClassPause);
        setPollId(null);
        return;
      }

      updateFirestoreDoc("sessions", sessionId, {
        pollId: null,
        isClassPause,
      });
    },
    [sessionId, isPreview]
  );

  const closePollAndShowResults = useCallback(
    (pollId) => {
      if (!pollId) {
        throw new Error("cannot show poll results - no poll id");
      }

      if (!sessionId) {
        return;
      }

      if (isPreview) {
        setIsClassPause(true);
        setPollId(null);
        return;
      }

      updateFirestoreDoc("sessions", sessionId, {
        pollId: null,
        isClassPause: true,
      });
    },
    [sessionId, isPreview]
  );

  /** @todo update after renaming sessionPolls firestore collection */
  // show poll to players, open for responses (poll live)
  const openPoll = useCallback(
    (pollResults, pollId) => {
      if (!pollId) {
        throw new Error("cannot open poll - no poll id");
      }

      if (!sessionId) {
        return;
      }

      if (isPreview) {
        setIsClassPause(false);
        setPollId(pollId);
        return;
      }

      const initPollResults = new Promise((resolve, reject) => {
        if (pollId && !pollResults[pollId]) {
          updateFirestoreDoc("sessionPolls", sessionPollsId, {
            [`results.${pollId}`]: {},
          })
            .then(resolve)
            .catch(reject);
        } else {
          resolve();
        }
      });

      initPollResults.then(() => {
        updateFirestoreDoc("sessions", sessionId, {
          pollId,
          isClassPause: false,
        });
      });
    },
    [sessionId, sessionPollsId, isPreview]
  );

  /*
    SESSION SLIDES
  */

  useEffect(
    () =>
      sessionSlidesId
        ? getDocSnapshot(
            SESSION_SLIDES_COLLECTION,
            sessionSlidesId,
            (snapshot) => {
              setSessionSlidesData(snapshot.data());
            }
          )
        : () => {},
    [sessionSlidesId]
  );

  const {
    slideChangeInfo = {
      hideSlideDisplay: false,
      prevHiderSlideLabel: false, // can be false or label of slide if it should hide slide display
      newSlideNumber: null,
    },
  } = sessionSlidesData;

  const [currSlideNumber, setCurrSlideNumber] = useState(0);

  const updateSlideChangeInfo = useCallback(
    (slideChangeInfo) => {
      if (isPreview) {
        setSessionSlidesData((prev) => ({
          ...prev,
          slideChangeInfo: {
            ...prev.slideChangeInfo,
            ...slideChangeInfo,
          },
        }));
        return;
      }
      if (sessionSlidesId) {
        updateFirestoreDoc(
          SESSION_SLIDES_COLLECTION,
          sessionSlidesId,
          mapKeys(slideChangeInfo, (_value, key) => `slideChangeInfo.${key}`)
        );
      }
    },
    [sessionSlidesId, isPreview]
  );

  // note: current poll data is omitted because it's managed locally and not by the database
  return {
    currSlideNumber,
    isClassPause,
    lessonId,
    pollQuestions: mapValues(pollsData, "question"),
    pollResults,
    sessionStatsId,
    setCurrSlideNumber,
    slideChangeInfo,
    updateSlideChangeInfo,
    playerStatus: pollId
      ? PLAYER_STATUS.POLLING
      : isClassPause
      ? PLAYER_STATUS.PAUSED
      : PLAYER_STATUS.PLAYING,
    currPollNumCompleted: pollId ? pollsNumCompleted[pollId] : null,
    customizationNumCompleted,
    setPauseAndClearPrevPoll,
    closePollAndShowResults,
    openPoll,
  };
};

export default useSession;

const getPollResults = (results, pollsData) =>
  mapValues(results, (pollQuestionObj, pollId) => {
    const maybePollData = pollsData[pollId];
    // if the poll id from the online database does not match local poll data
    // (presumably due to online database corruption),
    // do not process that set of results
    if (!maybePollData) {
      return [];
    }
    const { choices, correctChoiceId } = maybePollData;
    const noPollResponses = transform(
      choices,
      (acc, val) => {
        acc[val.choiceId] = 0;
      },
      {}
    );
    return transform(
      {
        ...noPollResponses,
        ...countBy(Object.values(pollQuestionObj)),
      },
      (questionData, votes, choiceId) => {
        questionData.push({
          choiceId,
          votes,
          choiceText: choices[Number(choiceId) - 1].text,
          correct: choiceId === correctChoiceId,
        });
      },
      []
    );
  });
